import { useState, useEffect } from 'react';
import { User, BarChart3, Key, Plus, Copy, Trash2, Loader2, X, Shield, AlertTriangle, Crown, Mail } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../stores/authStore';
import { tokenApi, vipApi, authApi } from '../utils/api';
import { useFormattedDate } from '../i18n/dateUtils';
import type { AccessToken } from '../types';

const Profile = () => {
  const { t } = useTranslation('profile');
  const { t: tCommon } = useTranslation('common');
  const { formatDate } = useFormattedDate();
  const { user, updateUser } = useAuthStore();
  const [showApiKey, setShowApiKey] = useState(false);

  // VIP 兑换码状态
  const [vipCode, setVipCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [redeemMsg, setRedeemMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Access Token 状态
  const [tokens, setTokens] = useState<AccessToken[]>([]);
  const [tokensLoading, setTokensLoading] = useState(true);
  const [tokensError, setTokensError] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [tokenName, setTokenName] = useState('');
  const [creating, setCreating] = useState(false);
  const [newToken, setNewToken] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const [revokingId, setRevokingId] = useState<number | null>(null);

  const fetchTokens = async () => {
    setTokensLoading(true);
    setTokensError('');
    try {
      const res = await tokenApi.list();
      setTokens(res.data.tokens || []);
    } catch (err: any) {
      setTokensError(err?.response?.data?.detail || t('accessTokens.fetchError'));
    } finally {
      setTokensLoading(false);
    }
  };

  useEffect(() => {
    fetchTokens();
  }, []);

  const handleCreate = async () => {
    if (!tokenName.trim()) return;
    setCreating(true);
    setNewToken(null);
    try {
      const res = await tokenApi.create(tokenName.trim());
      setNewToken(res.data.token.token);
      setTokenName('');
      fetchTokens();
    } catch (err: any) {
      alert(err?.response?.data?.detail || t('accessTokens.createModal.error'));
    } finally {
      setCreating(false);
    }
  };

  const handleRevoke = async (tokenId: number) => {
    if (!confirm(t('accessTokens.revokeConfirm'))) return;
    setRevokingId(tokenId);
    try {
      await tokenApi.revoke(tokenId);
      fetchTokens();
    } catch (err: any) {
      alert(err?.response?.data?.detail || t('accessTokens.revokeError'));
    } finally {
      setRevokingId(null);
    }
  };

  const handleCopy = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // fallback
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const stats = [
    {
      label: t('usageQuota.dailyQuota'),
      value: user?.daily_quota.toString(),
      icon: BarChart3,
    },
    {
      label: t('usageQuota.usedToday'),
      value: user?.used_today.toString(),
      icon: BarChart3,
    },
    {
      label: t('usageQuota.remaining'),
      value: user?.remaining.toString(),
      icon: BarChart3,
    },
  ];

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-600 mt-1">{t('description')}</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* 基本信息 */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <User className="w-5 h-5" />
            {t('basicInfo.title')}
          </h2>
          <div className="space-y-4">
            <div>
              <label className="text-sm text-gray-500">{t('basicInfo.usernameLabel')}</label>
              <p className="text-lg font-medium">{user?.username}</p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{t('basicInfo.roleLabel')}</label>
              <p className="text-lg font-medium">
                {user?.role === 'admin' ? tCommon('roles.admin') : user?.role === 'vip' ? tCommon('roles.vip') : tCommon('roles.user')}
              </p>
            </div>
            <div>
              <label className="text-sm text-gray-500">{t('basicInfo.createdAtLabel')}</label>
              <p className="text-lg font-medium">
                {user?.created_at
                  ? formatDate(user.created_at)
                  : '-'}
              </p>
            </div>
          </div>
        </div>

        {/* 使用配额 */}
        <div className="card">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            {t('usageQuota.title')}
          </h2>
          <div className="space-y-4">
            {stats.slice(1).map((stat) => {
              const Icon = stat.icon;
              return (
                <div key={stat.label} className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gray-100 rounded-lg">
                      <Icon className="w-4 h-4 text-gray-600" />
                    </div>
                    <span className="text-gray-600">{stat.label}</span>
                  </div>
                  <span className="text-xl font-bold text-gray-900">
                    {stat.value}
                  </span>
                </div>
              );
            })}
          </div>

          {/* 配额进度条 */}
          <div className="mt-6">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-gray-600">{t('usageQuota.todayProgress')}</span>
              <span className="text-gray-900">
                {user?.used_today} / {user?.daily_quota}
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className="bg-primary-600 h-2 rounded-full transition-all"
                style={{
                  width: `${
                    user
                      ? (user.used_today / user.daily_quota) * 100
                      : 0
                  }%`,
                }}
              />
            </div>
          </div>
        </div>

        {/* VIP 升级引导 */}
        {user?.role !== 'vip' && user?.role !== 'admin' && (
          <div className="card">
            <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
              <Crown className="w-5 h-5 text-amber-500" />
              {t('vip.upgradeTitle')}
            </h2>
            <p className="text-sm text-gray-600 mb-4">
              {t('vip.desc')}
            </p>

            {/* 套餐信息 */}
            <div className="space-y-3 mb-5">
              <div className="flex items-center justify-between p-3 rounded-lg bg-gray-50 border border-gray-100">
                <div>
                  <p className="text-sm font-medium text-gray-900">{t('vip.monthlyPlan')}</p>
                  <p className="text-xs text-gray-500">{t('vip.monthlyQuota')}</p>
                </div>
                <span className="text-sm font-bold text-gray-900">{t('vip.monthlyPrice')}</span>
              </div>
              <div className="flex items-center justify-between p-3 rounded-lg bg-amber-50 border border-amber-200">
                <div>
                  <p className="text-sm font-medium text-amber-900">{t('vip.lifetimePlan')}</p>
                  <p className="text-xs text-amber-600">{t('vip.lifetimeDesc')}</p>
                </div>
                <span className="text-sm font-bold text-amber-900">{t('vip.lifetimePrice')}</span>
              </div>
            </div>

            {/* 联系方式 */}
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-100">
              <p className="text-sm font-medium text-blue-900 mb-1">{t('vip.howToActivate')}</p>
              <p className="text-sm text-blue-700 mb-3">
                {t('vip.howToActivateDesc')}
              </p>
              <a
                href="mailto:aivoice@qq.com?subject=VIP会员开通申请&body=用户名："
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-500 text-white text-sm font-semibold rounded-lg transition-colors"
              >
                <Mail className="w-4 h-4" />
                {t('vip.sendEmail')}
              </a>
            </div>

            {/* 兑换码输入 */}
            <div className="mt-5 pt-5 border-t border-gray-100">
              <p className="text-sm text-gray-500 mb-3">{t('vip.alreadyHaveCode')}</p>
              <div className="flex items-center gap-3">
                <input
                  type="text"
                  value={vipCode}
                  onChange={(e) => { setVipCode(e.target.value); setRedeemMsg(null); }}
                  placeholder={t('vip.redeemPlaceholder')}
                  className="input flex-1 font-mono"
                  maxLength={50}
                />
                <button
                  onClick={async () => {
                    if (!vipCode.trim()) return;
                    setRedeeming(true);
                    setRedeemMsg(null);
                    try {
                      await vipApi.redeem(vipCode.trim());
                      // 从服务端获取最新配额，确保数据准确
                      const profileRes = await authApi.getProfile();
                      updateUser({
                        role: 'vip',
                        daily_quota: profileRes.data.daily_quota,
                        used_today: profileRes.data.used_today,
                        remaining: profileRes.data.remaining,
                      });
                      setRedeemMsg({ type: 'success', text: t('vip.redeemSuccess') });
                      setVipCode('');
                    } catch (err: any) {
                      setRedeemMsg({ type: 'error', text: err?.response?.data?.detail || t('vip.redeemFailed') });
                    } finally {
                      setRedeeming(false);
                    }
                  }}
                  disabled={redeeming || !vipCode.trim()}
                  className="btn-primary flex items-center gap-2 disabled:opacity-50"
                >
                  {redeeming ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />}
                  {t('vip.redeemButton')}
                </button>
              </div>
              {redeemMsg && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${redeemMsg.type === 'success' ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'}`}>
                  {redeemMsg.text}
                </div>
              )}
            </div>
          </div>
        )}

        {user?.role === 'vip' && (
          <div className="card bg-amber-50 border-amber-200">
            <div className="flex items-center gap-3">
              <Crown className="w-8 h-8 text-amber-500" />
              <div>
                <p className="font-semibold text-amber-900">{t('vip.currentVipTitle')}</p>
                <p className="text-sm text-amber-700">{t('vip.currentVipDesc', { quota: user?.daily_quota })}</p>
              </div>
            </div>
          </div>
        )}

        {/* API Key */}
        <div className="card md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Key className="w-5 h-5" />
            {t('apiKey.title')}
          </h2>
          <p className="text-sm text-gray-600 mb-4">
            {t('apiKey.description')}
          </p>
          <div className="flex items-center gap-4">
            <div className="flex-1 p-3 bg-gray-100 rounded-lg font-mono text-sm">
              {showApiKey
                ? useAuthStore.getState().apiKey
                : '••••••••••••••••••••••••••••••'}
            </div>
            <button
              onClick={() => setShowApiKey(!showApiKey)}
              className="btn-secondary"
            >
              {showApiKey ? t('apiKey.hide') : t('apiKey.show')}
            </button>
          </div>
        </div>

        {/* Access Tokens */}
        <div className="card md:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Shield className="w-5 h-5" />
              {t('accessTokens.title')}
            </h2>
            <button
              onClick={() => setShowCreateModal(true)}
              className="btn-primary flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {t('accessTokens.createButton')}
            </button>
          </div>
          <p className="text-sm text-gray-600 mb-4">
            {t('accessTokens.description')}
          </p>

          {tokensLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
            </div>
          ) : tokensError ? (
            <div className="text-red-500 text-sm py-4">{tokensError}</div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-8 text-gray-400">
              <Shield className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>{t('accessTokens.empty')}</p>
              <p className="text-sm mt-1">{t('accessTokens.emptyHint')}</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">{t('accessTokens.table.name')}</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">{t('accessTokens.table.token')}</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">{t('accessTokens.table.status')}</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">{t('accessTokens.table.createdAt')}</th>
                    <th className="text-left py-3 px-2 text-gray-500 font-medium">{t('accessTokens.table.lastUsed')}</th>
                    <th className="text-right py-3 px-2 text-gray-500 font-medium">{t('accessTokens.table.actions')}</th>
                  </tr>
                </thead>
                <tbody>
                  {tokens.map((token) => (
                    <tr key={token.id} className="border-b border-gray-100 hover:bg-gray-50">
                      <td className="py-3 px-2 font-medium">{token.name}</td>
                      <td className="py-3 px-2 font-mono text-xs text-gray-500">{token.token_prefix}</td>
                      <td className="py-3 px-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                          token.is_active
                            ? 'bg-green-100 text-green-700'
                            : 'bg-red-100 text-red-700'
                        }`}>
                          {token.is_active ? tCommon('status.active') : tCommon('status.revoked')}
                        </span>
                      </td>
                      <td className="py-3 px-2 text-gray-500">
                        {formatDate(token.created_at)}
                      </td>
                      <td className="py-3 px-2 text-gray-500">
                        {token.last_used_at
                          ? formatDate(token.last_used_at)
                          : '-'}
                      </td>
                      <td className="py-3 px-2 text-right">
                        {token.is_active && (
                          <button
                            onClick={() => handleRevoke(token.id)}
                            disabled={revokingId === token.id}
                            className="text-red-500 hover:text-red-700 disabled:opacity-50 inline-flex items-center gap-1"
                          >
                            {revokingId === token.id ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              <Trash2 className="w-4 h-4" />
                            )}
                            {t('accessTokens.table.revoke')}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* 使用说明 */}
        <div className="card md:col-span-2">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">
            {t('usageGuide.title')}
          </h2>
          <div className="space-y-3 text-sm text-gray-600">
            <p>{t('usageGuide.step1')}</p>
            <p>{t('usageGuide.step2')}</p>
            <p>{t('usageGuide.step3')}</p>
            <p>{t('usageGuide.step4')}</p>
            <p>{t('usageGuide.step5')}</p>
            <p>{t('usageGuide.step6')}</p>
            <p>{t('usageGuide.step7')}</p>
          </div>
        </div>
      </div>

      {/* 创建 Token 弹窗 */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            {!newToken ? (
              <>
                {/* 创建表单 */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{t('accessTokens.createModal.title')}</h3>
                    <button
                      onClick={() => { setShowCreateModal(false); setTokenName(''); }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>
                  <p className="text-sm text-gray-600 mb-4">
                    {t('accessTokens.createModal.description')}
                  </p>
                  <input
                    type="text"
                    value={tokenName}
                    onChange={(e) => setTokenName(e.target.value)}
                    placeholder={t('accessTokens.createModal.namePlaceholder')}
                    className="input w-full mb-4"
                    maxLength={100}
                    onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                  />
                  <div className="flex justify-end gap-3">
                    <button
                      onClick={() => { setShowCreateModal(false); setTokenName(''); }}
                      className="btn-secondary"
                    >
                      {t('accessTokens.createModal.cancel')}
                    </button>
                    <button
                      onClick={handleCreate}
                      disabled={!tokenName.trim() || creating}
                      className="btn-primary flex items-center gap-2 disabled:opacity-50"
                    >
                      {creating ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <Plus className="w-4 h-4" />
                      )}
                      {t('accessTokens.createModal.create')}
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* 创建成功 - 显示 Token */}
                <div className="p-6">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold text-gray-900">{t('accessTokens.createSuccess.title')}</h3>
                    <button
                      onClick={() => { setShowCreateModal(false); setNewToken(null); }}
                      className="text-gray-400 hover:text-gray-600"
                    >
                      <X className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4 flex items-start gap-2">
                    <AlertTriangle className="w-5 h-5 text-amber-500 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-amber-700">
                      {t('accessTokens.createSuccess.warning')}
                    </p>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <label className="text-xs text-gray-500 block mb-1">{t('accessTokens.createSuccess.tokenLabel')}</label>
                    <div className="flex items-center gap-2">
                      <code className="flex-1 text-xs font-mono break-all text-gray-800">
                        {newToken}
                      </code>
                      <button
                        onClick={() => handleCopy(newToken)}
                        className="btn-secondary flex-shrink-0 flex items-center gap-1 text-xs"
                      >
                        <Copy className="w-3.5 h-3.5" />
                        {copied ? t('accessTokens.createSuccess.copied') : t('accessTokens.createSuccess.copy')}
                      </button>
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-3 mb-4">
                    <label className="text-xs text-gray-500 block mb-1">{t('accessTokens.createSuccess.usageExample')}</label>
                    <code className="text-xs font-mono text-gray-800 block">
                      curl -H "Authorization: Bearer {newToken}" \<br />
                      &nbsp;&nbsp;https://your-api-host/tts/voice-design
                    </code>
                  </div>

                  <div className="flex justify-end">
                    <button
                      onClick={() => { setShowCreateModal(false); setNewToken(null); }}
                      className="btn-primary"
                    >
                      {t('accessTokens.createSuccess.closeButton')}
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Profile;
